import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Alert, Card, Descriptions, Table, Space, Row, Col, Button, Typography, Grid, Modal } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Order, OrderItem } from "../model/types";
import { PATHS } from "../../../app/router/paths";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface OrderConfirmPageProps {
  order?: Order;
  isDesktop?: boolean;
  onModify?: () => void;
  onConfirm?: () => void;
}

export const OrderConfirmPage: React.FC<OrderConfirmPageProps> = ({
  order: propOrder,
  isDesktop: propIsDesktop,
  onModify,
  onConfirm,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();

  const isDesktop = propIsDesktop ?? !!screens.md;
  const targetOrder = propOrder || (location.state as { order?: Order } | null)?.order;

  const handleModify = () => {
    if (onModify) {
      onModify();
    } else {
      navigate(PATHS.ORDER_CREATE);
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      Modal.success({
        title: "注文が確定しました",
        content: targetOrder ? `注文番号: ${targetOrder.orderNo} で受付を行いました。` : "注文の受付を行いました。",
        onOk: () => {
          navigate(PATHS.ORDERS);
        },
      });
    }
  };

  if (!targetOrder) {
    return (
      <Card style={{ borderRadius: 10 }}>
        <Space direction="vertical" style={{ width: "100%", textAlign: "center", padding: "32px 0" }}>
          <Title level={4}>確認対象の注文情報がありません</Title>
          <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate(PATHS.ORDERS)}>
            注文一覧へ戻る
          </Button>
        </Space>
      </Card>
    );
  }

  const order = targetOrder;

  return (
    <div style={{ padding: isDesktop ? "16px" : "12px", maxWidth: 1200, margin: "0 auto", minHeight: "100vh" }}>
      <Alert
        message="注意事項: 納品日および数量の確認を行ってください。"
        description="注文確定後のキャンセル・数量変更には対応できない場合があります。"
        type="warning"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      <Card title="注文内容の確認" style={{ marginBottom: 16, borderRadius: 10 }}>
        <Descriptions title="基本情報" bordered column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="店舗・顧客名">{order.customerName}</Descriptions.Item>
          <Descriptions.Item label="注文日">{order.orderDate}</Descriptions.Item>
          <Descriptions.Item label="納品希望日">{order.deliveryDate}</Descriptions.Item>
          <Descriptions.Item label="備考">{order.notes || "なし"}</Descriptions.Item>
        </Descriptions>

        <Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
          注文商品明細
        </Title>

        {isDesktop ? (
          <Table
            dataSource={order.items}
            rowKey="id"
            pagination={false}
            columns={[
              { title: "商品コード", dataIndex: "productCode" },
              { title: "商品名", dataIndex: "productName" },
              {
                title: "単価",
                dataIndex: "unitPrice",
                render: (val: number) => `¥${val.toLocaleString()}`,
              },
              {
                title: "内訳 (S/M/L/XL/3L)",
                render: (_: any, record: OrderItem) =>
                  `S:${record.sizes.S} / M:${record.sizes.M} / L:${record.sizes.L} / XL:${record.sizes.XL} / 3L:${record.sizes["3L"]}`,
              },
              {
                title: "合計数量",
                render: (_: any, record: OrderItem) =>
                  Object.values(record.sizes as Record<string, number>).reduce((a: number, b: number) => a + b, 0),
              },
              {
                title: "小計",
                render: (_: any, record: OrderItem) => {
                  const qty = Object.values(record.sizes as Record<string, number>).reduce((a: number, b: number) => a + b, 0);
                  return `¥${(qty * record.unitPrice).toLocaleString()}`;
                },
              },
            ]}
          />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {order.items.map((item: OrderItem, idx: number) => {
              const qty = Object.values(item.sizes as Record<string, number>).reduce((a: number, b: number) => a + b, 0);
              return (
                <div
                  key={idx}
                  style={{
                    padding: 12,
                    background: "#fafafa",
                    borderRadius: 8,
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <Text strong style={{ display: "block" }}>
                    {item.productName} ({item.productCode})
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, display: "block", margin: "4px 0" }}>
                    数量内訳: S:{item.sizes.S} | M:{item.sizes.M} | L:{item.sizes.L} | XL:
                    {item.sizes.XL} | 3L:{item.sizes["3L"]} (計 {qty}点)
                  </Text>
                  <Text strong style={{ color: "#1677ff" }}>
                    小計: ¥{(qty * item.unitPrice).toLocaleString()}
                  </Text>
                </div>
              );
            })}
          </Space>
        )}

        <Row justify="end" style={{ marginTop: 24 }}>
          <Col style={{ textAlign: "right" }}>
            <Text type="secondary">小計: ¥{(order.totalAmount * 0.9).toFixed(0)}</Text>
            <br />
            <Text type="secondary">消費税 (10%): ¥{(order.totalAmount * 0.1).toFixed(0)}</Text>
            <br />
            <Title level={3} style={{ marginTop: 8, color: "#1677ff" }}>
              総計金額: ¥{order.totalAmount.toLocaleString()}
            </Title>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginTop: 24 }}>
          <Button onClick={handleModify}>修正する</Button>
          <Button type="primary" size={isDesktop ? "large" : "middle"} onClick={handleConfirm}>
            注文を確定する
          </Button>
        </Row>
      </Card>
    </div>
  );
};


export default OrderConfirmPage;

